import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import app, { db } from './firebase';

/**
 * Notifications push (FCM) — côté client.
 *
 * Pré-requis pour l'activation :
 *  - REACT_APP_FCM_VAPID_KEY : clé "Web Push certificates" (console Firebase →
 *    Paramètres du projet → Cloud Messaging).
 *  - Un envoyeur (Cloud Function) qui pousse un message à chaque création de
 *    document `notifications` en ciblant les `fcmTokens` du destinataire.
 *
 * Sans clé VAPID, `isPushConfigured()` renvoie false et le bouton d'activation
 * reste inactif : aucun comportement cassé.
 */

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY;

export const isPushConfigured = () => Boolean(VAPID_KEY);

/**
 * Demande la permission, enregistre le service worker, récupère le jeton FCM
 * et l'ajoute à `users/{userId}.fcmTokens`.
 * @returns {Promise<string>} le jeton enregistré
 */
export const enablePush = async (userId) => {
  if (!VAPID_KEY) {
    throw new Error('Notifications push non configurées (clé VAPID manquante).');
  }
  if (!(await isSupported())) {
    throw new Error("Les notifications push ne sont pas supportées sur cet appareil.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permission de notification refusée.');
  }

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration
  });

  if (token && userId) {
    await updateDoc(doc(db, 'users', userId), { fcmTokens: arrayUnion(token) });
  }
  return token;
};
